import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { LOCAL_STORAGE_NAMES, UI_LANGUAGE_TAGS } from '../constants/constants';

interface LanguageContextProps {
  language: string;
  changeLanguage: (lang: string) => void;
  getAppLanguageCode: () => string;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<string>(() => {
    return localStorage.getItem(LOCAL_STORAGE_NAMES.language) ?? 'en';
  });

  const changeLanguage = useCallback((lang: string) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem(LOCAL_STORAGE_NAMES.language, lang);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);
  const getAppLanguageCode = useCallback(() => UI_LANGUAGE_TAGS[language] || 'en-US', [language]);

  const languageContextValue = useMemo(
    () => ({ language, changeLanguage, getAppLanguageCode }),
    [language, changeLanguage, getAppLanguageCode],
  );
  return (
    <LanguageContext.Provider value={languageContextValue}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
